/**
 * DrManiReadingCounter - UX-5, the excitement engine.
 *
 * A small persistent chip near the progress bar: a sprout mark plus
 * "300 readings on the table." After every answer the number animates down
 * (a quick tick over about 500ms, with the eliminated difference briefly
 * flashing, e.g. minus 40) and a one-line narration appears under it.
 * The count lands on exactly 1 at the final answer, shown as "1 reading, locked".
 */
import React, { useEffect, useRef, useState } from 'react';
import { dmmCounterLabel } from '../../data/drManiQuiz/uxCopy';
import { DmmSproutMark } from './DrManiIcons';

const TICK_MS = 500;

interface DrManiReadingCounterProps {
  count: number;
  narration: string | null;
  /** How many readings the latest answer eliminated (null when snapping, e.g. on Back). */
  delta: number | null;
  /** Increments once per drop so the flash and pulse restart every time. */
  tick: number;
}

const DrManiReadingCounter: React.FC<DrManiReadingCounterProps> = ({
  count,
  narration,
  delta,
  tick,
}) => {
  const [display, setDisplay] = useState(count);
  const [pulsing, setPulsing] = useState(false);
  const previousCount = useRef(count);

  useEffect(() => {
    const from = previousCount.current;
    const to = count;
    if (from === to) return undefined;
    previousCount.current = to;

    // Snap upward changes (Back navigation) and non-animatable environments.
    if (to > from || typeof window.requestAnimationFrame !== 'function') {
      setDisplay(to);
      return undefined;
    }

    setPulsing(true);
    // The start time comes from the first frame's timestamp so the animation
    // stays on one clock (real rAF or fake timers in tests).
    let startedAt: number | null = null;
    let frame = 0;
    const step = (now: number) => {
      if (startedAt === null) startedAt = now;
      const progress = Math.min(1, (now - startedAt) / TICK_MS);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (progress < 1) frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    const pulseTimer = window.setTimeout(() => setPulsing(false), TICK_MS + 60);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(pulseTimer);
      setDisplay(to);
    };
  }, [count]);

  const locked = count === 1;

  return (
    <div className="dmm-counter">
      <div
        className={`dmm-counter-chip ${pulsing ? 'dmm-counter-chip-pulse' : ''} ${locked ? 'dmm-counter-locked' : ''}`}
      >
        <span className="dmm-counter-mark" aria-hidden="true">
          <DmmSproutMark />
        </span>
        <span className="dmm-counter-number">{dmmCounterLabel(display)}</span>
        {delta !== null && delta > 0 && (
          <span key={tick} className="dmm-counter-delta" aria-hidden="true">
            minus {delta}
          </span>
        )}
      </div>
      {narration && (
        <p key={narration} className="dmm-counter-narration" aria-live="polite">
          {narration}
        </p>
      )}
    </div>
  );
};

export default DrManiReadingCounter;
